import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, GripHorizontal } from 'lucide-react';

const DraggableItem = ({
  id,
  x,
  y,
  width,
  height,
  minWidth = 120,
  minHeight = 100,
  isSelected,
  onSelect,
  onMove,
  onResize,
  onDelete,
  onInteractionStart,
  onInteractionEnd,
  clientToWorld,
  authorLabel = '',
  children,
  headerColor = 'bg-gray-50',
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [ghostRect, setGhostRect] = useState(null);
  const dragSessionRef = useRef(null);
  const resizeSessionRef = useRef(null);

  const handleRootPointerDown = useCallback((e) => {
    // Never allow canvas to hijack draggable item interactions.
    e.stopPropagation();
  }, []);

  const finishDrag = useCallback((commit = true) => {
    const session = dragSessionRef.current;
    if (!session || session.finished) return;
    session.finished = true;
    session.cleanup?.();
    dragSessionRef.current = null;
    setIsDragging(false);
    setGhostRect(null);

    if (commit && session.didMutate && session.latestRect) {
      onMove(id, session.latestRect.x, session.latestRect.y);
    }
    onInteractionEnd?.({ didMutate: Boolean(commit && session.didMutate), mode: 'move' });
  }, [id, onInteractionEnd, onMove]);

  const finishResize = useCallback((commit = true) => {
    const session = resizeSessionRef.current;
    if (!session || session.finished) return;
    session.finished = true;
    session.cleanup?.();
    resizeSessionRef.current = null;
    setIsResizing(false);
    setGhostRect(null);

    if (commit && session.didMutate && session.latestRect) {
      onResize?.(id, session.latestRect.width, session.latestRect.height);
    }
    onInteractionEnd?.({ didMutate: Boolean(commit && session.didMutate), mode: 'resize' });
  }, [id, onInteractionEnd, onResize]);

  useEffect(() => () => {
    finishDrag(false);
    finishResize(false);
  }, [finishDrag, finishResize]);

  const handleHeaderPointerDown = useCallback((e) => {
    if (typeof e.button === 'number' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    onInteractionStart?.();
    setIsDragging(true);
    onSelect(id, e);

    const startWorld = clientToWorld?.(e.clientX, e.clientY) || { x, y };
    const session = {
      pointerId: typeof e.pointerId === 'number' ? e.pointerId : null,
      startWorldX: startWorld.x,
      startWorldY: startWorld.y,
      startX: x,
      startY: y,
      width,
      height,
      didMutate: false,
      finished: false,
      latestRect: { x, y, width, height },
      cleanup: null,
    };
    dragSessionRef.current = session;
    setGhostRect(session.latestRect);

    const handlePointerMove = (moveEvent) => {
      if (!dragSessionRef.current || dragSessionRef.current.finished) return;
      if (
        session.pointerId != null &&
        typeof moveEvent.pointerId === 'number' &&
        moveEvent.pointerId !== session.pointerId
      ) return;
      if (moveEvent.pointerType === 'touch' || moveEvent.pointerType === 'pen') {
        moveEvent.preventDefault();
      }
      const nextWorld = clientToWorld?.(moveEvent.clientX, moveEvent.clientY);
      if (!nextWorld) return;
      const newX = session.startX + (nextWorld.x - session.startWorldX);
      const newY = session.startY + (nextWorld.y - session.startWorldY);
      if (Math.abs(newX - x) > 0.2 || Math.abs(newY - y) > 0.2) {
        session.didMutate = true;
      }
      session.latestRect = { x: newX, y: newY, width, height };
      setGhostRect(session.latestRect);
    };

    const handlePointerEnd = () => finishDrag(true);
    const handleWindowBlur = () => finishDrag(true);
    const handleVisibilityChange = () => {
      if (document.hidden) finishDrag(true);
    };
    const options = true;

    session.cleanup = () => {
      window.removeEventListener('pointermove', handlePointerMove, options);
      window.removeEventListener('pointerup', handlePointerEnd, options);
      window.removeEventListener('pointercancel', handlePointerEnd, options);
      window.removeEventListener('mouseup', handlePointerEnd, options);
      window.removeEventListener('blur', handleWindowBlur, options);
      document.removeEventListener('visibilitychange', handleVisibilityChange, options);
    };

    window.addEventListener('pointermove', handlePointerMove, options);
    window.addEventListener('pointerup', handlePointerEnd, options);
    window.addEventListener('pointercancel', handlePointerEnd, options);
    window.addEventListener('mouseup', handlePointerEnd, options);
    window.addEventListener('blur', handleWindowBlur, options);
    document.addEventListener('visibilitychange', handleVisibilityChange, options);
  }, [clientToWorld, finishDrag, height, id, onInteractionStart, onSelect, width, x, y]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onSelect(id, e);
  }, [id, onSelect]);

  const handleDelete = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(id);
  }, [id, onDelete]);

  const handleResizePointerDown = useCallback((e) => {
    if (!onResize) return;
    if (typeof e.button === 'number' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    onInteractionStart?.();
    onSelect(id, e);
    setIsResizing(true);

    const startWorld = clientToWorld?.(e.clientX, e.clientY) || { x: e.clientX, y: e.clientY };
    const session = {
      pointerId: typeof e.pointerId === 'number' ? e.pointerId : null,
      width: width || 0,
      height: height || 0,
      startWorldX: startWorld.x,
      startWorldY: startWorld.y,
      didMutate: false,
      finished: false,
      latestRect: { x, y, width, height },
      cleanup: null,
    };
    resizeSessionRef.current = session;
    setGhostRect(session.latestRect);

    const handlePointerMove = (moveEvent) => {
      if (!resizeSessionRef.current || resizeSessionRef.current.finished) return;
      if (
        session.pointerId != null &&
        typeof moveEvent.pointerId === 'number' &&
        moveEvent.pointerId !== session.pointerId
      ) return;
      if (moveEvent.pointerType === 'touch' || moveEvent.pointerType === 'pen') {
        moveEvent.preventDefault();
      }
      const nextWorld = clientToWorld?.(moveEvent.clientX, moveEvent.clientY);
      if (!nextWorld) return;
      const deltaX = nextWorld.x - session.startWorldX;
      const deltaY = nextWorld.y - session.startWorldY;
      const nextWidth = Math.max(minWidth, session.width + deltaX);
      const nextHeight = Math.max(minHeight, session.height + deltaY);
      if (Math.abs(nextWidth - (width || 0)) > 0.2 || Math.abs(nextHeight - (height || 0)) > 0.2) {
        session.didMutate = true;
      }
      session.latestRect = { x, y, width: nextWidth, height: nextHeight };
      setGhostRect(session.latestRect);
    };

    const handlePointerEnd = () => finishResize(true);
    const handleWindowBlur = () => finishResize(true);
    const handleVisibilityChange = () => {
      if (document.hidden) finishResize(true);
    };
    const options = true;

    session.cleanup = () => {
      window.removeEventListener('pointermove', handlePointerMove, options);
      window.removeEventListener('pointerup', handlePointerEnd, options);
      window.removeEventListener('pointercancel', handlePointerEnd, options);
      window.removeEventListener('mouseup', handlePointerEnd, options);
      window.removeEventListener('blur', handleWindowBlur, options);
      document.removeEventListener('visibilitychange', handleVisibilityChange, options);
    };

    window.addEventListener('pointermove', handlePointerMove, options);
    window.addEventListener('pointerup', handlePointerEnd, options);
    window.addEventListener('pointercancel', handlePointerEnd, options);
    window.addEventListener('mouseup', handlePointerEnd, options);
    window.addEventListener('blur', handleWindowBlur, options);
    document.addEventListener('visibilitychange', handleVisibilityChange, options);
  }, [clientToWorld, finishResize, height, id, minHeight, minWidth, onInteractionStart, onResize, onSelect, width, x, y]);

  return (
    <>
      <div
        className={`wb-draggable-item ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''} ${ghostRect ? 'ghosting' : ''} ${className}`}
        style={{
          position: 'absolute',
          left: x,
          top: y,
          width,
          height,
          zIndex: isSelected ? 1000 : 1,
        }}
        onClick={handleClick}
        onPointerDown={handleRootPointerDown}
        onWheel={(e) => e.stopPropagation()}
      >
        <div
          className={`wb-draggable-header ${headerColor}`}
          onPointerDown={handleHeaderPointerDown}
        >
          <GripHorizontal size={14} className="wb-drag-handle" />
          <button
            type="button"
            className="wb-delete-button"
            onClick={handleDelete}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            title="Delete"
          >
            <X size={14} />
          </button>
        </div>

        <div className="wb-draggable-content">
          {children}
        </div>

        {authorLabel && (
          <div className="wb-element-author-tag">{authorLabel}</div>
        )}

        {isSelected && onResize && (
          <button
            type="button"
            className="wb-resize-handle"
            onPointerDown={handleResizePointerDown}
            aria-label="Resize item"
            title="Resize"
          />
        )}
      </div>
      {ghostRect && (
        <div
          className="wb-draggable-ghost"
          style={{
            left: ghostRect.x,
            top: ghostRect.y,
            width: ghostRect.width,
            height: ghostRect.height,
            zIndex: 1500,
          }}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default DraggableItem;
